import { gql, useQuery } from '@apollo/client';
import { Loader, Panel } from '@gasbuddy/react-components';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import classnames from 'classnames/bind';
import { getBrandMatchesForStationBrands, getStationDisplayName } from '../../../lib/utils/station';
import BadgeAds from '../BadgeAds';
import EnterpriseBadge from '../EnterpriseBadge';
import FavoriteStationIcon from '../FavoriteStationIcon';
import StarRating from '../StarRating';
import StationBrandings from './StationBrandings';
import StationDisplayName from './StationDisplayName';
import StationHours from './StationHours';
import styles from './StationInfoBox.module.css';

const cx = classnames.bind(styles);

export const GET_STATION = gql`
  query Station($stationId: ID!) {
    station(id: $stationId) {
      address {
        line_1
        line_2
        locality
        region
        postal_code
      }
      badges
      brandings {
        brand_id
        branding_type
      }
      brands {
        brand_id
        name
        image_url
      }
      enterprise
      hours {
        opening_hours
        status
        next_intervals {
          open
          close
        }
      }
      id
      name
      phone
      ratings_count
      star_rating
    }
  }
`;

export default function StationInfoBox({
  basic,
  noBadges,
  noContainer,
  noFavoriting,
  noHours,
  noPhone,
  noRating,
  singleLineAddress,
  stationId,
}) {
  const { loading: isLoading, error, data: stationData } = useQuery(GET_STATION, {
    variables: { stationId },
  });
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const {
    address,
    badges: badgeIds,
    brandings,
    brands,
    enterprise: isEnterprise,
    hours,
    name: stationName,
    phone,
    ratings_count: numRatings,
    star_rating: rating,
  } = stationData?.station || {};

  const handleHoursToggle = useCallback(() => {
    setIsHoursOpen(!isHoursOpen);
  }, [isHoursOpen]);

  const stationBrands = getBrandMatchesForStationBrands(brandings, brands);

  return (
    <Panel
      className={cx('stationInfoContainer')}
      color="white"
      bordered={!noContainer}
      padded={false}
      rounded
    >
      {isLoading
        ? (<Loader style={{ width: '100%' }} />)
        : (
          <React.Fragment>
            <div className={cx('logoColumn')}>
              {stationName && (
                <StationBrandings
                  brands={stationBrands}
                  stationName={getStationDisplayName(brands, stationName)}
                  containerClass={cx('largeLogoContainer')}
                />
              )}
            </div>
            <div className={cx('mainInfo')}>
              {!error && (
                <StationDisplayName
                  brands={stationBrands}
                  stationName={stationName}
                  as="h2"
                  headerClass={cx('header')}
                  subheaderClass={cx('stationNameSubHeader')}
                  headerBadge={!basic && !noBadges && isEnterprise && (
                    <EnterpriseBadge />
                  )}
                />
              )}
              {!error && !noRating && (
                <React.Fragment>
                  <StarRating
                    name={`station${stationId}Rating`}
                    size="large"
                    rating={rating}
                  />
                  &nbsp;
                  <span className={cx('ratings')}>
                    {`(${numRatings || 0})`}
                  </span>
                </React.Fragment>
              )}
              {!error && (
                <React.Fragment>
                  <div className={cx('regular')}>
                    <div className={cx('ellipsisNoWrap')}>
                      {!!address
                        && (
                          <span>
                            <span>{address.line_1 ? address.line_1 : null}</span>
                            <span>{address.line_2 ? address.line_2 : null}</span>
                            <span>
                              {singleLineAddress ? ' - ' : <br />}
                              {address.locality ? address.locality : null}
                              ,&nbsp;
                              {address.region ? address.region : null}
                              &nbsp;
                            </span>
                          </span>
                        )
                      }
                    </div>
                  </div>
                  {!basic && (
                    <React.Fragment>
                      {!noBadges && badgeIds?.length > 0 && (
                        <BadgeAds badgeIds={badgeIds} />
                      )}
                      {!noPhone && phone && (
                        <div className={cx('phone')}>
                          <a href={`tel:${phone}`} className={cx('phoneLink')}>{phone}</a>
                        </div>
                      )}
                      {!noHours && hours && hours.status && (
                        <div style={{ position: 'relative' }}>
                          <StationHours
                            hours={hours}
                            isHoursOpen={isHoursOpen}
                            onChange={handleHoursToggle}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </div>
            {!basic && !noFavoriting && (
              <div className={cx('favorite')}>
                <FavoriteStationIcon stationId={stationId} verbose />
              </div>
            )}
          </React.Fragment>
        )
      }
    </Panel>
  );
}

StationInfoBox.propTypes = {
  // Whether to show only the basic station info (brand, rating, and address)
  basic: PropTypes.bool,
  // Whether to show badges
  noBadges: PropTypes.bool,
  // Whether to show in an outlined container
  noContainer: PropTypes.bool,
  // Whether to show the favoriting functionality
  noFavoriting: PropTypes.bool,
  // Whether to show station hours if available
  noHours: PropTypes.bool,
  // Whether to show the station phone number
  noPhone: PropTypes.bool,
  // Whether to show rating stars
  noRating: PropTypes.bool,
  // Whether to show the station address on a single line or not
  singleLineAddress: PropTypes.bool,
  stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

StationInfoBox.defaultProps = {
  basic: false,
  noBadges: false,
  noContainer: false,
  noFavoriting: false,
  noHours: false,
  noPhone: false,
  noRating: false,
  singleLineAddress: false,
};
